import _ from 'lodash';
import { getCustomUserName, strParamReplacer, getAdditionalFlags, getAdditionalFlagValuesFromStorage } from 'Utils';
import { RULE_ACTION_TYPE, SIMULATION_STATUS } from 'Common/enums';
import { AxiosResponse } from 'axios';
import { DateService, configService, ListNamesDictionary } from 'Services';
import { InterpolationOptions, TFunction } from 'i18next';

interface TransFormServerDataProps {
    t: TFunction;
    users?: UsersHashmap;
    listNameMap?: ListNamesDictionary;
    parameterData?: UseGetParametersResponse;
}
interface TransformationConclusionProps extends TransFormServerDataProps {
    setFlagActions?: Rule['actions'];
    addToListActions?: Rule['actions'];
    outputDescription: Rule['outputDescription'];
    actions?: Rule['actions'];
}

/**
 *
 * @param obj an object to apply the square brack transformation on
 * @param keys the list of strings we want to utilize to "transform"
 * @param paramPath the path to access the "parameters" in the obj
 * @returns Object
 */
export const paramHelper = <D extends { [key: string]: any | null }, P extends keyof D>(
    obj: D,
    keys: P[],
    paramPath: P
) => {
    if (obj === null) return {};
    return keys.reduce((acc, curr) => {
        const gottenValue: string | undefined = _.get(obj, curr);
        return {
            ...acc,
            [curr]: strParamReplacer(gottenValue, {
                type: 'parameterMap',
                parameterMap: _.get(obj, paramPath)
            })
        };
    }, {});
};

const transformationConclusion = ({
    actions,
    setFlagActions,
    addToListActions,
    outputDescription,
    t,
    listNameMap,
    parameterData
}: TransformationConclusionProps) => {
    if (!actions) return outputDescription;

    const conclusionFlagListData: AdditionalConclusionValues[] = getAdditionalFlagValuesFromStorage();
    const setFlags = setFlagActions?.map((action) => {
        const flags = getAdditionalFlags(conclusionFlagListData, action.values as string[]);
        return ` ${t('And Set Flags')} [${flags.join(', ')}]`;
    });

    const addToList = addToListActions?.map((action) => {
        const { fieldName, listIds } = action.values as AddToList;
        const message = parameterData?.fieldNameHashmap[fieldName]?.message;
        const listNames: string[] = listIds.map((listId) => {
            return listNameMap?.[listId] || t('Network List');
        });
        return ` ${t('And Add Parameter')} [${message}] ${t('to List')} [${listNames.join(', ')}]`;
    });

    return outputDescription + setFlags?.join('') + addToList?.join('');
};

/**
 * @note The Transform Server Data function is looping by page load and on every click event.
 * The process is causing a delay or freeze the UI.
 */
export const transformServerData = ({ t, users, listNameMap, parameterData }: TransFormServerDataProps) => (
    data: GlobalRulesData
) => {
    const { config, rules } = data;

    return {
        ...data,
        config: {
            ...config,
            _formatted: {
                ...(config?._formatted ?? {}),
                ...paramHelper(config ?? {}, ['description', 'inputDescription'], 'parameters')
            }
        },
        rules: rules.map((rule) => {
            const {
                allowOverrides,
                hiddenFromTenant,
                isTenantOverride,
                ruleGroup,
                _formatted,
                createdAt,
                status,
                parameters,
                createdBy,
                actions,
                outputDescription
            } = rule;

            const setFlagActions = actions?.filter(({ operator }) => operator === RULE_ACTION_TYPE.SET_FLAGS);
            const addToListActions = actions?.filter(({ operator }) => operator === RULE_ACTION_TYPE.ADD_TO_LIST);
            const updateScoreByActions = actions?.filter(
                (action) => action.operator === RULE_ACTION_TYPE.UPDATE_SCORE_BY
            );

            return {
                ...rule,
                allowOverridesString: allowOverrides ? t('Yes') : t('No'),
                hiddenFromTenantString: hiddenFromTenant ? t('Yes') : t('No'),
                // added this derived field because allowOverrides to be boolean and string(Filtering)
                virtualRuleGroup: isTenantOverride ? 'Overrides' : ruleGroup,
                _formatted: {
                    ...(_formatted ?? {}),
                    ...paramHelper(rule, ['description', 'inputDescription'], 'parameters'),
                    createdAt: DateService.getDateTime(createdAt) ?? createdAt,
                    // eslint-disable-next-line no-nested-ternary
                    active: status ? t(status) : parameters.active ? t('Active') : t('Inactive'),
                    createdBy: getCustomUserName(createdBy, users),
                    outputDescription: transformationConclusion({
                        actions,
                        setFlagActions,
                        addToListActions,
                        outputDescription,
                        t,
                        listNameMap,
                        parameterData
                    }),
                    conclusionValues: {
                        updateScoreBy: updateScoreByActions,
                        setFlags: setFlagActions,
                        addToList: addToListActions?.map((action) => {
                            const actionValues = action.values as AddToList;
                            const listNames: string[] = actionValues.listIds.map(
                                (listId) => listNameMap?.[listId] || t('Network List')
                            );
                            return {
                                ...action,
                                values: {
                                    ...actionValues,
                                    listIds: listNames,
                                    fieldName: actionValues.fieldName,
                                    message: parameterData?.fieldNameHashmap[actionValues.fieldName]?.message
                                }
                            };
                        })
                    }
                }
            };
        })
    };
};

export const transformMerchantOverrides = (formatFunc: (str: string) => string) => (
    res: AxiosResponse<MerchantOverrideDataItem[]>
) => {
    return res?.data.map((dataItem) => {
        const { aggregateData, merchantId, modifiedFlag, canBeDeleted } = dataItem;
        return {
            ...aggregateData,
            merchantId,
            modifiedFlag,
            canBeDeleted,
            _formatted: {
                lastModified: DateService.getDateTime(aggregateData.lastModified) ?? aggregateData.lastModified,
                merchantEnabled: aggregateData?.merchantEnabled ? formatFunc('Active') : formatFunc('Inactive')
            }
        };
    });
};

export const transformReports = (reportsData: AxiosResponse<ReportDataItem[]>, users?: UsersHashmap) => {
    const reportTransformer = (dataItem: ReportDataItem) => {
        const { properties, id, createdAt, createdBy } = dataItem;
        const { dateRange, dateRangeLabel } = properties || {};

        return {
            ...properties,
            ...dateRange,
            id,
            createdAt,
            createdBy,
            _formatted: {
                createdAt: DateService.getDateTime(createdAt) ?? createdAt,
                dateRangeLabel: DateService.getReportDateRange(
                    dateRange?.dateRangeType,
                    dateRange?.dateRangeValue,
                    dateRangeLabel
                ),
                createdBy: getCustomUserName(createdBy, users)
            }
        };
    };
    return reportsData?.data.map(reportTransformer);
};

export const modifySimulationStatus = (str: string) => {
    return str.split('_').join(' ');
};

export const transformSimulations = (t: TFunction, res: SimulationApiResponseObj[], users?: UsersHashmap) => {
    const simulationTransformer = (dataItem: SimulationApiResponseObj) => {
        const {
            start,
            end,
            createdBy,
            compare,
            isNewSimulation,
            simulationType,
            status,
            createdAt,
            ...rest
        } = dataItem;
        const compareFlag = compare?.length ? t('Yes') : '';
        const duration = DateService.getDuration(start, end);
        return {
            ...rest,
            start,
            end,
            createdBy,
            compareFlag,
            isNewSimulation,
            createdAt,
            simulationType,
            status,
            _formatted: {
                duration,
                createdBy: getCustomUserName(createdBy, users),
                isNewSimulation: dataItem.status === SIMULATION_STATUS.FINISHED && isNewSimulation,
                status: modifySimulationStatus(status),
                simulationType: modifySimulationStatus(simulationType),
                start,
                createdAt
            }
        };
    };
    return res?.map(simulationTransformer);
};

const nestedStringKey = (basePath: string) => (str: string) => {
    return `${basePath}.${str}`;
};

export const getTranslationErrorHandling = (t: TFunction, nestedKey?: string) => (
    s: string,
    options?: InterpolationOptions & { [key: string]: any }
) => {
    const key = nestedKey ? `errorHandling.${nestedKey}` : 'errorHandling';
    return t(nestedStringKey(key)(s), { ...options });
};

export const getTranslationValidationHandling = (t: TFunction) => (
    s: string,
    options?: InterpolationOptions & { [key: string]: any }
) => {
    return t(nestedStringKey('validation')(s), { ...options });
};

// okta returns user's email as id whereas mcconnect returns userId as id
export const getFormattedDisplayName = (user: UserAPIResponseObj) => {
    const { lastName, firstName, id } = user;

    if (firstName && lastName) {
        return `${lastName}, ${firstName}`;
    }
    if (firstName || lastName) {
        return lastName || firstName;
    }

    if (!(firstName && lastName)) {
        return id;
    }

    return '';
};

// create a hashmap of users with ids as keys
export const getUsersHashMap = (usersData: UsersAPIResponse) => {
    const genUsersWithUsername = (acc: UsersHashmap, curr: UserAPIResponseObj) => {
        return {
            ...acc,
            [curr.id]: { ...curr, username: getFormattedDisplayName(curr) }
        };
    };

    const usersObj = usersData.reduce(genUsersWithUsername, {});

    return usersObj;
};

export const getCustomList = (listType?: string) => {
    const { customListTypes } = configService;

    const configCustomList: ConfigCustomListObj = customListTypes?.find(
        (customList: ConfigCustomListObj) => customList.customListType === listType
    )!;

    return configCustomList;
};

export const transformTenants = (t: TFunction, res: Tenants) => {
    const tenantTransformer = (dataItem: Tenant) => {
        const { allowTenantAccess, ...rest } = dataItem;
        const tenantAccess = allowTenantAccess ? t('Yes') : t('No');
        return {
            ...rest,
            allowTenantAccess,
            tenantAccess
        };
    };
    return res?.map(tenantTransformer);
};

export const getRulePriority = (t: TFunction, rule: Rule) => {
    return rule?.nonTemplated ? 1 : t('N/A');
};
